import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react'; // Import useRef
import { configureApi, initializeFirebaseAuth } from '../services/apiService';
import { getStoredAuth, setStoredAuth, completeLogout, needsValidation } from '../utils/auth';
import { User, UserRole, AuthState, AuthContextState, StoredAuthData } from '../types/auth'; // Ensure User is imported
import Log from '../log'; // Import Log for logging purposes

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  sessionId: string | null;
  loading: boolean;
  error: string | null;
  login: (authProvider: 'twitter' | 'google', useBff?: boolean, context?: Record<string, string>) => Promise<void>; 
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  updateRole: (role: UserRole) => void;
  loginId: [string, string, string, string, number, string] | null;
  getAuthHeaders: () => Record<string, string>;
  refreshToken: (sessionId?: string, token?: string, inRegister?: boolean) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
  baseUrl: string;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, baseUrl }) => {
  configureApi(baseUrl); // Configure the API base URL for all requests
  
  // Store initial search params to preserve them on logout redirection
  const initialSearchParamsRef = useRef(window.location.search);

  const storedAuth = getStoredAuth();
  const v_needed = needsValidation(storedAuth);
  console.log('Stored auth data found:', storedAuth, 'Validation needed:', v_needed);
  
  // Initialize with proper state structure
  const [auth, setAuth] = useState<AuthState | null>(() => {
    if (v_needed || !storedAuth) return null;
    
        
    return {
      user: storedAuth.user,
      token: storedAuth.token,
      sessionId: storedAuth.sessionId,
      loading: false,
      error: null
    };
  });
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loginId, setLoginId] = useState<[string, string, string, string, number, string] | null>(() => {
    if (v_needed || !storedAuth?.user) return null;
    
    return [
      storedAuth.user.loginId || '',
      storedAuth.user.userName || '',
      storedAuth.user.smaName || '',
      storedAuth.user.smaSince || '',
      storedAuth.user.level || 0,
      storedAuth.user.picture || ''
    ];
  });

  // Extract refreshToken as a standalone function
  const refreshToken = useCallback(async (sessionId?: string, token?: string, inRegister?: boolean): Promise<boolean> => {
    try {
      // Use provided sessionId/token or fall back to the ones in auth state
      // console.log('DEBUG: AuthContext: Refreshing token...', sessionId || 'None', token || 'None');
      // Show stack trace for debugging
      // console.trace('DEBUG: AuthContext: Refreshing token stack trace');
      const storedAuth = getStoredAuth();
      const v_needed_chk = needsValidation(storedAuth);

      if (!sessionId && !token && !v_needed_chk) {
        console.info('No need for refresh as existing sessionId and token are available and valid');
        Log.set(storedAuth?.user?.loginId, storedAuth?.sessionId);
        return true;
      }

      // Check if sessionId and token are provided, otherwise use auth state
      const effectiveSessionId = sessionId || auth?.sessionId;
      const effectiveToken = token || auth?.token;
      
      if (!effectiveSessionId) {
        console.error('Cannot refresh token: No session ID available');
        return false;
      }
      
      const headers: Record<string, string> = {};
      if (effectiveToken) {
        headers['Authorization'] = `Bearer ${effectiveToken}`;
        headers['Access-Control-Allow-Headers'] = 'Authorization';
      }

      // console.log('DEBUG: AuthContext: Refreshing token with sessionId:');
      const response = await fetch(`${baseUrl}?verifyToken&sessionId=${effectiveSessionId}`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include', // Include cookies for BFF authentication
        headers
      });
      // console.log('DEBUG: AuthContext: Finished token refresh request:');
      
      if (!response.ok) {
        // If token validation fails, logout
        if (response.status === 401) {
          logout();
          return false;
        }
        throw new Error(`Failed to refresh token: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.valid && data.payload && (data.payload.role !== 'unregistered' || inRegister)) {
        // Create updated auth state with proper structure
        const updatedAuth: AuthState = {
          user: data.payload,
          token: data.payload.token || effectiveToken, // Use existing token if not provided in payload
          sessionId: effectiveSessionId,
          loading: false,
          error: null,
          lastValidated: Date.now() // Add lastValidated timestamp
        };
        
        // Update stored auth
        setStoredAuth({
          user: data.payload,
          token: updatedAuth.token,
          sessionId: effectiveSessionId,
          lastValidated: Date.now() // Add lastValidated timestamp
        });
        
        setAuth(updatedAuth);
        
        // Update loginId
        if (data.payload) {
          setLoginId([
            data.payload.loginId || '',
            data.payload.userName || '',
            data.payload.smaName || '',
            data.payload.smaSince || '',
            data.payload.level || 0,
            data.payload.picture || ''
          ]);
        }
        Log.set(storedAuth?.user?.loginId, storedAuth?.sessionId);
        return true;
      } else {
        logout();
        return false;
      }
    } catch (err) {
      console.error('Token refresh error:', err);
      return false;
    }
  }, [auth?.token, auth?.sessionId, baseUrl]);

  // This comment explains the key issues with front-side authentication vs BFF pattern
  
  /* Security considerations for both popup and redirect authentication methods:
   *
   * Popup-based OAuth (current implementation):
   * 1. Still exposes tokens to browser JavaScript environment
   * 2. Requires client-side storage of sensitive tokens (localStorage/sessionStorage)
   * 3. Vulnerable to malicious postMessage attacks if origin validation is incomplete
   * 4. Users often block popups, leading to authentication failures
   * 5. Popup window can be spoofed by malicious sites to steal credentials
   * 6. Can't use HTTP-only cookies, making tokens vulnerable to XSS attacks
   * 7. Client secrets can't be securely stored
   *
   * Redirect-based OAuth (without BFF):
   * 1. Breaks single-page application user experience with full page reloads
   * 2. Still exposes tokens to browser JavaScript environment
   * 3. State parameter must be carefully managed to prevent CSRF attacks
   * 4. Token is typically stored in browser storage, accessible to any JavaScript
   * 5. URL fragments with tokens can be logged in browser history
   *
   * BFF Pattern (server-mediated auth) solves these issues by:
   * 1. Keeping tokens and secrets server-side, never exposing them to the client
   * 2. Using HTTP-only, secure cookies for session management
   * 3. Handling token refresh server-side
   * 4. Providing consistent authentication regardless of browser popup settings
   * 5. Centralizing authentication logic in a controlled server environment
   *
   * Both direct popup and redirect methods are less secure than the BFF pattern,
   * as they both expose tokens to the client-side JavaScript environment.
   */
  
  // Update login signature to accept context
  const login = async (authProvider: 'twitter' | 'google', useBff: boolean = false, context: Record<string, string> = {}): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      console.log('Starting login process...', useBff ? 'using BFF' : 'direct', 'Context:', context);
      
      if (useBff) {
        // BFF-based authentication flow for Python backend
        
        // Get CSRF token if needed (common in Django)
        baseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl; // Ensure no trailing slash
        let csrfToken = '';
        try {
          // This request fetches a CSRF token from the backend
          // The token proves that the subsequent request comes from our legitimate frontend
          const csrfResponse = await fetch(`${baseUrl}/api/csrf-token`, {
            method: 'GET',
            credentials: 'include', // Ensures cookies are sent with the request
          });
          if (csrfResponse.ok) {
            const csrfData = await csrfResponse.json();
            csrfToken = csrfData.csrfToken;
          }
        } catch (csrfErr) {
          console.log('No CSRF protection or endpoint not available');
        }
        
        // --- Start: Revised URL Construction ---
        
        const origin = window.location.origin;
        const pathname = window.location.pathname;
        const hash = window.location.hash; // e.g., #/some/path?query=val
        const search = window.location.search; // e.g., ?outside=val

        // 1. Parse existing search params
        const existingSearchParams = new URLSearchParams(search);

        // 2. Parse existing hash path and hash query params
        let hashPath = '';
        const existingHashParams = new URLSearchParams(); // Only hash query params go here
        if (hash) { // Check if hash exists
            const hashContent = hash.substring(1); // Remove '#'
            const hashParts = hashContent.split('?'); 
            hashPath = hashParts[0]; // e.g., /some/path or just path
            if (hashParts.length > 1) {
                const hashQuery = hashParts[1]; // e.g., query=val
                const parsedHashParams = new URLSearchParams(hashQuery);
                parsedHashParams.forEach((value, key) => {
                    existingHashParams.set(key, value); // Store existing hash params
                });
            }
        }
        // Ensure hashPath starts with / if it exists, otherwise it's empty
        const cleanHashPath = hashPath ? (hashPath.startsWith('/') ? hashPath : `/${hashPath}`) : '';

        // 3. Create final search params (existing + context) - These go in the main URL query
        const finalHashParams = new URLSearchParams(existingHashParams);
        Object.entries(context).forEach(([key, value]) => {
          if (value) {
            finalHashParams.set(key, value); // Add context to main search params
          }
        });

        // 4. Create final cancel search params (final + authCancelled) - Also go in main URL query
        const finalCancelHashParams = new URLSearchParams(finalHashParams);
        finalCancelHashParams.set('authCancelled', 'true');

        // 5. Construct the base URL (origin + pathname)
        const baseUrlString = origin + pathname;

        // 6. Construct main search strings
        const finalHashString = finalHashParams.toString();
        const finalCancelHashString = finalCancelHashParams.toString();

        // 7. Construct the hash part (using only original hash path and original hash query params)
        const existingSearchQueryString = existingSearchParams.toString();
        // Start with #, add the path, then add ? and query params if they exist
        const hashPart = `#${cleanHashPath}${finalHashString ? `?${finalHashString}` : ''}`;
        // Handle case where hash is just '#' or empty
        const finalHashPart = (cleanHashPath || finalHashString) ? hashPart : ''; 
        const cancelHashPart = `#${cleanHashPath}${finalCancelHashString ? `?${finalCancelHashString}` : ''}`;
        const finalCancelHashPart = (cleanHashPath || finalCancelHashString) ? cancelHashPart : '';

        // 8. Combine for returnUrlWithContext and cancelUrlWithContext
        const returnUrlWithContext = `${baseUrlString}${existingSearchQueryString ? `?${existingSearchQueryString}` : ''}${finalHashPart}`;
        const cancelUrlWithContext = `${baseUrlString}${existingSearchQueryString ? `?${existingSearchQueryString}` : ''}${finalCancelHashPart}`;

        console.log('Constructed Return URL:', returnUrlWithContext);
        console.log('Constructed Cancel URL:', cancelUrlWithContext);
        // alert('Return URL: ' + returnUrlWithContext + '\nCancel URL: ' + cancelUrlWithContext); // Debugging alert - Remove or comment out

        // --- End: Revised URL Construction ---

        // Encode the URLs for the backend redirect
        const encodedReturnUrl = encodeURIComponent(returnUrlWithContext);
        const encodedCancelUrl = encodeURIComponent(cancelUrlWithContext);

        // Construct the auth URL for the backend
        const authUrl = `${baseUrl}/api/auth/${authProvider}?redirect_uri=${encodedReturnUrl}&cancel_uri=${encodedCancelUrl}`;
        
        const finalUrl = csrfToken ? `${authUrl}&csrfToken=${csrfToken}` : authUrl;
        
        // Check if we're returning from a cancelled authentication
        // Check main search params first
        const currentUrlParams = new URLSearchParams(window.location.search); 
        let authCancelled = currentUrlParams.get('authCancelled') === 'true';
        
        // No need to check hash for authCancelled anymore as it's always in main search

        if (authCancelled) {
          // Clean up the URL (remove authCancelled and context params from main search)
          const cleanSearchParams = new URLSearchParams(window.location.search);
          cleanSearchParams.delete('authCancelled');
          Object.keys(context).forEach(key => cleanSearchParams.delete(key)); 
          const cleanSearchString = cleanSearchParams.toString();

          // Keep the original hash untouched
          const originalHash = window.location.hash;
          
          // Use replaceState to clean URL without reload
          window.history.replaceState({}, document.title, 
              pathname + (cleanSearchString ? `?${cleanSearchString}` : '') + originalHash
          );

          setLoading(false);
          setError('Authentication was cancelled.');
          return;
        }
        
        // Redirect to Python backend auth endpoint
        console.log('Redirecting to BFF:', finalUrl);
        window.location.href = finalUrl;
        return; // The page will reload after authentication
      }
      
      // // Direct OAuth flow (original implementation)
      // const response = await fetch(`${baseUrl}?loginUrl&authp=${authProvider}`, {
      //   method: 'GET',
      //   mode: 'cors',
      // });
      // if (!response.ok) {
      //   throw new Error(`Failed to get login URL: ${response.status}`);
      // }
      
      // const data = await response.json();
      // const sessionId = data.sessionId;
      
      // // Open OAuth popup window with properly formatted features
      // // Note: Feature string must not have spaces after commas for some browsers
      // const popup = window.open(
      //   data.url, 
      //   'Login',
      //   'width=600,height=700,resizable=yes,scrollbars=yes,status=yes'
      // );
      
      // // Check if popup was blocked
      // if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      //   throw new Error('Popup blocked. Please allow popups for this site.');
      // }
      
      // // Try to focus the popup (some browsers require this)
      // try {
      //   popup.focus();
      // } catch (e) {
      //   console.warn('Could not focus popup window');
      // }
      
      // // Set up message event listener for AUTH_SUCCESS
      // const messagePromise = new Promise<void>((resolve, reject) => {
      //   const handleAuthMessage = (event: MessageEvent) => {
      //     console.log("Received message:", event.data);
          
      //     // Handle explicit cancellation message from oauth provider
      //     if (event.data && event.data.type === 'AUTH_CANCELLED') {
      //       console.log('Auth cancelled message received');
      //       window.removeEventListener('message', handleAuthMessage);
      //       reject(new Error('Authentication was cancelled.'));
      //       return;
      //     }
          
      //     if (event.data && event.data.type === 'AUTH_SUCCESS') {
      //       console.log('Auth success message received:', event.data);
            
      //       // Remove the message listener
      //       window.removeEventListener('message', handleAuthMessage);
      //       window.removeEventListener('message', handleCancelMessage);
            
      //       // Handle the successful authentication
      //       if (event.data.user) {
      //         // Parse user data if it's a string (from JSON.stringify)
      //         let userData: User;
      //         try {
      //           userData = typeof event.data.user === 'string' 
      //             ? JSON.parse(event.data.user) 
      //             : event.data.user;
                
      //           // Validate that userData conforms to User type
      //           if (!userData.loginId || !userData.userName) {
      //             throw new Error('Invalid user data structure');
      //           }
                
      //           // Create auth object with proper structure
      //           const newAuth: AuthState = {
      //             user: userData,
      //             token: userData.token || '', // Handle token being in user object or separate
      //             sessionId: sessionId,
      //             loading: false,
      //             error: null,
      //             lastValidated: Date.now() // Add lastValidated timestamp
      //           };
                
      //           // Store auth data
      //           setStoredAuth({
      //             user: userData,
      //             token: userData.token || '',
      //             sessionId: sessionId,
      //             lastValidated: Date.now() // Add lastValidated timestamp to stored data
      //           });
                
      //           setAuth(newAuth);
                
      //           // Update loginId for backward compatibility
      //           setLoginId([
      //             userData.loginId,
      //             userData.userName,
      //             userData.smaName || '',
      //             userData.smaSince || '',
      //             userData.level || 0,
      //             userData.picture || ''
      //           ]);
                
      //           // Initialize Firebase Auth if we have a firebase_token
      //           if (userData.firebase_token) {
      //             initializeFirebaseAuth().catch(err => {
      //               console.error('Error initializing Firebase Auth after login:', err);
      //             });
      //           }
                
      //           resolve();
      //         } catch (parseError) {
      //           console.error('Error parsing user data:', parseError);
      //           reject(new Error('Invalid user data format'));
      //         }
      //       } else {
      //         reject(new Error('No user data received'));
      //       }
      //     }
      //   };
        
      //   // Listen for authentication cancellation
      //   const handleCancelMessage = (event: MessageEvent) => {
      //     if (event.data && (event.data.type === 'AUTH_CANCEL' || event.data.type === 'AUTH_ERROR')) {
      //       console.log('Auth cancel/error message received:', event.data);
      //       window.removeEventListener('message', handleAuthMessage);
      //       reject(new Error('Login window was closed'));
      //     }
      //   };
        
      //   // Register message listeners
      //   window.addEventListener('message', handleAuthMessage);
      //   window.addEventListener('message', handleCancelMessage);
        
      //   // Set timeout to prevent hanging
      //   setTimeout(() => {
      //     window.removeEventListener('message', handleAuthMessage);
      //     window.removeEventListener('message', handleCancelMessage);
          
      //     // Try to close the popup, but don't check if it's closed
      //     try {
      //       popup.close();
      //     } catch (e) {
      //       console.log('Could not close popup, but continuing anyway');
      //     }
      //     reject(new Error('Login timed out'));
      //   }, 300000); // 5 minutes timeout
      // });
      
      // // Wait for authentication to complete via message or timeout
      // await messagePromise;
      // setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
      console.error('Login error:', err);
      setLoading(false);
      throw err; // Re-throw to allow component-level error handling
    }
  };

  const logout = useCallback(() => {
    console.log('DEBUG: Logging out...');
    setAuth(null);
    setLoginId(null);
    Log.set(null, null); // Clear log context
    completeLogout(); // Clear stored auth data

    // Redirect to the app origin, preserving original search parameters
    const redirectUrl = `${window.location.origin}${initialSearchParamsRef.current}`;
    console.log('DEBUG: Redirecting to:', redirectUrl);
    window.location.replace(redirectUrl); // Use replace to avoid adding to history
    // alert('You have been logged out.');

  }, []); // No dependencies needed as it uses ref and window location

  const updateUser = useCallback((userData: Partial<User>) => {
    if (!auth?.user) return;

    const updatedUser = { ...auth.user, ...userData };
    
    // Create updated auth state with proper structure
    const updatedAuth: AuthState = {
      ...auth,
      user: updatedUser
    };
    
    // Update stored auth
    const storedAuthData: StoredAuthData = {
      user: updatedUser,
      token: auth.token,
      sessionId: auth.sessionId
    };
    
    setStoredAuth(storedAuthData);
    setAuth(updatedAuth);
    
    // Update loginId if it exists
    if (loginId) {
      setLoginId([
        updatedUser.loginId || '',
        updatedUser.userName || '',
        updatedUser.smaName || '',
        updatedUser.smaSince || '',
        updatedUser.level || 0,
        updatedUser.picture || ''
      ]);
    }
  }, [auth]);

  const updateRole = useCallback((role: UserRole) => {
    if (!auth?.user) return;
    updateUser({ role });
  }, [auth, updateUser]);

  // Use the extracted refreshToken in the effect
  useEffect(() => {
    if (!auth?.token || !auth?.sessionId) return;
    
    // Initial token refresh
    refreshToken();
    
    // Initialize Firebase Auth if we have a firebase_token
    if (auth.user?.firebase_token) {
      initializeFirebaseAuth().catch(err => {
        console.error('Error initializing Firebase Auth on token change:', err);
      });
    }
    
    // Set up periodic token refresh (every hour)
    const refreshInterval = setInterval(() => refreshToken(), 1 * 60 * 60 * 1000);
    
    return () => {
      clearInterval(refreshInterval);
    };
  }, [auth?.token, auth?.sessionId, refreshToken]);

  // Add getAuthHeaders method to the context
  const getAuthHeaders = useCallback((): Record<string, string> => {
    return auth?.token ? {
      'Authorization': `Bearer ${auth.token}`,
      'Access-Control-Allow-Headers': 'Authorization',
    } : {};
  }, [auth?.token]);

  const contextValue: AuthContextType = {
    isAuthenticated: !!auth?.token,
    user: auth?.user || null,
    token: auth?.token || null,
    sessionId: auth?.sessionId || null,
    loading: loading || auth?.loading || false,
    error: error || auth?.error || null,
    login,
    logout,
    updateUser,
    updateRole,
    loginId,
    getAuthHeaders,
    refreshToken // Add refreshToken to context
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
